import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../lib/api";
import BrandNav from "../components/BrandNav";
import ImageLightbox from "../components/ImageLightbox";
import {
  BookOpen, Headphones, Sparkles, FileText, Play, Pause, ChevronRight,
  Share2, GraduationCap,
} from "lucide-react";

const TAB_ORDER = ["age_6-7", "age_8-9", "age_10-12", "age_13-14", "age_15-17"];

function useCountUp(target, active, durationMs = 900) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active || !target) { setN(target || 0); return; }
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      // easeOutQuad
      const eased = 1 - (1 - p) * (1 - p);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, durationMs]);
  return n;
}

function Stat({ label, value, active }) {
  const n = useCountUp(value, active);
  return (
    <div className="ftg-showcase__stat" data-testid={`stat-${label}`}>
      <div className="ftg-showcase__stat-value">{n}</div>
      <div className="ftg-showcase__stat-label">{label}</div>
    </div>
  );
}

function InlinePlayer({ slug, audioUrl, testId }) {
  const { t } = useTranslation("common");
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };
  return (
    <button
      type="button"
      onClick={toggle}
      className={`ftg-showcase__mini-play ${playing ? "is-playing" : ""}`}
      data-testid={testId}
    >
      {playing ? <Pause size={16}/> : <Play size={16}/>}
      <span>{playing ? t("showcase.playing") : t("showcase.listen")}</span>
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />
    </button>
  );
}

export default function Showcase() {
  const { t } = useTranslation("common");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(TAB_ORDER[0]);
  const [isOwnerPreview, setIsOwnerPreview] = useState(false);
  // Lightbox for Reader covers — teachers project the cover on the
  // classroom screen. Clicking the cover image opens this; clicking the
  // rest of the card still opens the 2-page PDF sample.
  const [cover, setCover] = useState({ open: false, src: "", caption: "" });
  const coverTriggerRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/showcase/overview`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(r.statusText)))
      .then(setData)
      .catch((e) => setErr(String(e)));
    // Wait for the App-level ?preview= handshake (if any) before polling
    // the preview status so we don't race the cookie set.
    const ready = (typeof window !== "undefined" && window.__ftgOwnerPreviewReady)
      || Promise.resolve();
    ready.then(() =>
      fetch(`${API_BASE}/api/preview/status`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : { is_owner_preview: false })
        .then((j) => setIsOwnerPreview(!!j.is_owner_preview))
        .catch(() => {})
    );
  }, []);

  const activeBand = useMemo(
    () => (data?.bands || []).find((b) => b.slug === active),
    [data, active]
  );

  const shareWhatsapp = () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const text = encodeURIComponent(
      `Have a look at the FutureTec Global STEAM curriculum — 300 lessons, 20 heroes, 75 readers: ${url}`
    );
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener");
  };

  if (err) {
    return (
      <div>
        <BrandNav />
        <main className="ftg-shell ftg-page">
          <div className="ftg-verify__card ftg-verify__card--invalid" data-testid="showcase-error">
            <h1>{t("showcase.unavailable")}</h1><p>{err}</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-testid="showcase-page">
      <BrandNav />
      {isOwnerPreview && (
        <div className="ftg-owner-ribbon" data-testid="owner-preview-ribbon">
          <span className="ftg-owner-ribbon__dot" aria-hidden="true" />
          <span>{t("showcase.ownerRibbon")}</span>
          <a
            href={`${API_BASE}/api/preview/revoke`}
            className="ftg-owner-ribbon__revoke"
            data-testid="owner-preview-revoke"
          >
            {t("showcase.ownerRevoke")}
          </a>
        </div>
      )}
      <main className="ftg-shell ftg-page">

        {/* HERO */}
        <section className="ftg-showcase__hero">
          <div className="ftg-showcase__eyebrow">
            <Sparkles size={14}/> {t("showcase.eyebrow")}
          </div>
          <h1 className="ftg-showcase__title">
            {t("showcase.title")}
          </h1>
          <p className="ftg-showcase__lede">
            {t("showcase.lede")}
          </p>

          <div className="ftg-showcase__stats" data-testid="hero-stats">
            <Stat label={t("showcase.statLessons")}   value={data?.totals?.lessons   ?? 0} active={!!data}/>
            <Stat label={t("showcase.statHeroes")}    value={data?.totals?.dramas    ?? 0} active={!!data}/>
            <Stat label={t("showcase.statReaders")}   value={data?.totals?.readers   ?? 0} active={!!data}/>
            <Stat label={t("showcase.statWorkbooks")} value={data?.totals?.workbooks ?? 0} active={!!data}/>
          </div>
        </section>

        {/* TABS (sticky) */}
        <nav className="ftg-showcase__tabs" data-testid="showcase-tabs" aria-label="Age band">
          {(data?.bands || []).map((b) => (
            <button
              key={b.slug}
              type="button"
              onClick={() => setActive(b.slug)}
              className={`ftg-showcase__tab ${active === b.slug ? "is-active" : ""}`}
              data-testid={`showcase-tab-${b.slug}`}
            >
              {b.label_dash}
            </button>
          ))}
        </nav>

        {!activeBand && (
          <div className="ftg-loader" data-testid="showcase-loading"><div className="ftg-loader__dot"/></div>
        )}

        {activeBand && (
          <div className="ftg-showcase__band" data-testid={`band-${activeBand.slug}`}>

            {/* 1. Lessons */}
            <section className="ftg-showcase__section">
              <div className="ftg-showcase__section-head">
                <h2><GraduationCap size={18}/> {activeBand.lesson_count} audio lessons</h2>
                <p>3 terms &middot; 10 weeks &middot; 2 lessons per week. Titles &amp; outcomes only.</p>
              </div>
              {[1,2,3].map((tNum) => {
                const termLessons = activeBand.lessons.filter((l) => l.term === tNum);
                if (termLessons.length === 0) return null;
                return (
                  <div key={tNum} className="ftg-showcase__term-group">
                    <div className="ftg-showcase__term-title">Term {tNum}</div>
                    <div className="ftg-showcase__lessons">
                      {termLessons.map((l) => (
                        <div
                          key={`${l.term}-${l.week}-${l.lesson}`}
                          className="ftg-showcase__lesson"
                          data-testid={`lesson-${activeBand.slug}-t${l.term}-w${l.week}-l${l.lesson}`}
                        >
                          <div className="ftg-showcase__lesson-tag">W{l.week} · L{l.lesson}</div>
                          <div className="ftg-showcase__lesson-title">{l.title}</div>
                          {l.description ? (
                            <div className="ftg-showcase__lesson-desc">{l.description}</div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>

            {/* 2. Readers */}
            <section className="ftg-showcase__section">
              <div className="ftg-showcase__section-head">
                <h2><BookOpen size={18}/> {activeBand.reader_count} Deep-Dive Reader chapter-books</h2>
                <p>Fully illustrated 20-page storybooks. Click a cover to preview 2 pages &mdash; full books are for enrolled families.</p>
              </div>
              <div className="ftg-showcase__readers">
                {activeBand.readers.map((r) => (
                  <a
                    key={`${r.term}-${r.chapter_num}`}
                    href={`${API_BASE}${r.sample_url}`}
                    target="_blank" rel="noopener noreferrer"
                    className="ftg-showcase__reader"
                    data-testid={`reader-${activeBand.slug}-t${r.term}-c${r.chapter_num}`}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      className="ftg-showcase__reader-cover-btn"
                      aria-label={`Enlarge cover: ${r.title}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        coverTriggerRef.current = e.currentTarget;
                        setCover({
                          open: true,
                          src: `${API_BASE}${r.cover_url}`,
                          caption: `${r.title}${r.person_name ? ` \u2014 ${r.person_name}` : ""}`,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          coverTriggerRef.current = e.currentTarget;
                          setCover({
                            open: true,
                            src: `${API_BASE}${r.cover_url}`,
                            caption: `${r.title}${r.person_name ? ` \u2014 ${r.person_name}` : ""}`,
                          });
                        }
                      }}
                      data-testid={`reader-cover-open-${activeBand.slug}-t${r.term}-c${r.chapter_num}`}
                    >
                      <img src={`${API_BASE}${r.cover_url}`} alt={r.title} loading="lazy"/>
                    </span>
                    <div className="ftg-showcase__reader-meta">
                      <div className="ftg-showcase__reader-term">T{r.term} · Ch {r.chapter_num}</div>
                      <div className="ftg-showcase__reader-title">{r.title}</div>
                      {r.person_name ? (
                        <div className="ftg-showcase__reader-person">Meet {r.person_name}</div>
                      ) : null}
                      <div className="ftg-showcase__reader-sample-note">
                        Preview 2 pages · Full book for enrolled families
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </section>

            {/* 3. Audio dramas */}
            <section className="ftg-showcase__section">
              <div className="ftg-showcase__section-head">
                <h2><Headphones size={18}/> {activeBand.audio_drama_count} dramatised audio biographies</h2>
                <p>2 minutes each. Warm narration, gentle music.</p>
              </div>
              <div className="ftg-showcase__dramas">
                {activeBand.audio_dramas.map((d) => (
                  <div key={d.slug} className="ftg-showcase__drama"
                       data-testid={`drama-${activeBand.slug}-${d.slug}`}>
                    <div className="ftg-showcase__drama-body">
                      <div className="ftg-showcase__drama-name">{d.name}</div>
                      {d.tagline ? (
                        <div className="ftg-showcase__drama-tag">{d.tagline}</div>
                      ) : null}
                      <div className="ftg-showcase__drama-actions">
                        <InlinePlayer
                          slug={d.slug}
                          audioUrl={`${API_BASE}${d.audio_url}`}
                          testId={`drama-play-${d.slug}`}
                        />
                        <Link to={d.meet_page_url} className="ftg-showcase__drama-more"
                              data-testid={`drama-more-${d.slug}`}>
                          Full page <ChevronRight size={12}/>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 4. Workbooks */}
            <section className="ftg-showcase__section">
              <div className="ftg-showcase__section-head">
                <h2><FileText size={18}/> {activeBand.workbook_count} homework workbooks</h2>
                <p>Full workbooks stay with enrolled families &mdash; here's a stamped sample of page 1.</p>
              </div>
              <div className="ftg-showcase__workbooks">
                {activeBand.workbooks.map((w) => (
                  <div key={w.slug} className="ftg-showcase__workbook"
                       data-testid={`workbook-${w.slug}`}>
                    <img src={`${API_BASE}${w.cover_url}`} alt={w.title} loading="lazy"/>
                    <div className="ftg-showcase__workbook-body">
                      <div className="ftg-showcase__workbook-title">{w.title}</div>
                      <a
                        href={`${API_BASE}${w.sample_url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                        data-testid={`workbook-sample-${w.slug}`}
                      >
                        <FileText size={14}/> Preview 1st page (SAMPLE)
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* CTA BANNER */}
        <section className="ftg-showcase__cta" data-testid="showcase-cta">
          <div>
            <h2>{t("showcase.ctaTitle")}</h2>
            <p>{t("showcase.ctaSub")}</p>
          </div>
          <div className="ftg-showcase__cta-actions">
            <Link to="/#pricing" className="ftg-btn ftg-btn--secondary" data-testid="cta-pricing">
              {t("showcase.ctaPricing")} <ChevronRight size={14}/>
            </Link>
            <button type="button" onClick={shareWhatsapp} className="ftg-btn ftg-btn--ghost"
                    data-testid="cta-share">
              <Share2 size={14}/> {t("showcase.ctaShare")}
            </button>
          </div>
        </section>

        <footer className="ftg-showcase__footer">
          {t("showcase.footer")}
        </footer>
      </main>

      <ImageLightbox
        open={cover.open}
        onClose={() => setCover((c) => ({ ...c, open: false }))}
        src={cover.src}
        alt={cover.caption}
        caption={cover.caption}
        returnFocusRef={coverTriggerRef}
      />
    </div>
  );
}
